import api from '../../../shared/services/apiClient';
import { DataSource } from '@/types';

export const getDataSources = async (): Promise<DataSource[]> => {
  try {
    console.log('🔄 Fetching datasources from /api/datasources...');
    const res = await api.get('/datasources');
    console.log('✅ Datasources response:', res.data);
    return res.data as DataSource[];
  } catch (error: any) {
    console.error('❌ Error loading datasources:', error?.response?.status, error?.response?.data, error?.message);
    return []; // Return empty array instead of throwing
  }
};

export const getDataSourceById = async (id: string | number): Promise<DataSource | undefined> => {
  try {
    const res = await api.get(`/datasources/${id}`);
    return res.data as DataSource;
  } catch (error: any) {
    if (error?.response?.status === 404) {
      console.warn(`DataSource ${id} not found (404) - might have been deleted`);
      return undefined;
    }
    console.error('Error loading datasource:', error?.message);
    return undefined;
  }
};

export const createDataSource = async (data: Omit<DataSource, 'id' | 'lastUpdate'>): Promise<DataSource> => {
  const res = await api.post('/datasources', data);
  return res.data as DataSource;
};

export const updateDataSource = async (id: string | number, data: Partial<DataSource>): Promise<DataSource | undefined> => {
  const res = await api.put(`/datasources/${id}`, data);
  return res.data as DataSource;
};

export const deleteDataSource = async (id: string | number): Promise<boolean> => {
  const res = await api.delete(`/datasources/${id}`);
  return res.status === 204 || res.status === 200;
};

export const analyzeDataSource = async (id: string | number): Promise<any> => {
  try {
    const res = await api.post(`/datasources/${id}/analyze`);
    return res.data;
  } catch (error: any) {
    console.error('Error analyzing datasource:', error?.response?.status, error?.message);
    throw error;
  }
};

/**
 * Preview Google Sheets data without analyzing
 * Returns raw data for user confirmation before analysis
 */
export interface PreviewGoogleSheetsParams {
  spreadsheetId: string;
  sheetName: string;
  range?: string;
}

export interface PreviewGoogleSheetsResponse {
  success: boolean;
  data?: {
    headers: string[];
    rows: any[][];
    rowCount: number;
    totalRows: number;
    columnsCount: number;
    spreadsheetId: string;
    sheetName: string;
    range: string;
  };
  message?: string;
  errorType?: string;
  technicalError?: string;
}

export const previewGoogleSheets = async (params: PreviewGoogleSheetsParams): Promise<PreviewGoogleSheetsResponse> => {
  try {
    console.log('🔍 [API] Requesting preview for Google Sheets:', params);
    const res = await api.post('/datasources/preview', params);
    console.log('✅ [API] Preview response:', res.data);
    return res.data;
  } catch (error: any) {
    console.error('❌ [API] Error previewing Google Sheets:', error?.response?.data);
    // Return structured error response
    return {
      success: false,
      message: error?.response?.data?.message || error?.message || 'Error al previsualizar los datos',
      errorType: error?.response?.data?.errorType || 'UNKNOWN_ERROR',
      technicalError: error?.response?.data?.technicalError
    };
  }
};

// ============= API REST SPECIFIC FUNCTIONS =============

/**
 * Get all datasources with ACTIVE status and analysis (ready for dashboards)
 */
export const getActiveDatasources = async (): Promise<DataSource[]> => {
  try {
    const all = await getDataSources();
    return all.filter(ds => ds.status === 'ACTIVE' && ds.analysis);
  } catch (error: any) {
    console.error('Error loading active datasources:', error);
    return [];
  }
};

/**
 * Get datasources summary (for modeler and dashboard creation)
 */
export interface DataSourceSummary {
  id: string | number;
  name: string;
  type: string;
  status: string;
  records?: number;
  fields?: number;
  hasAnalysis: boolean;
  lastUpdate?: string;
}

export const getDatasources = async (): Promise<DataSourceSummary[]> => {
  try {
    const all = await getDataSources();
    return all.map(ds => ({
      id: ds.id,
      name: ds.name,
      type: ds.type,
      status: ds.status,
      records: ds.analysis?.rowCount || 0,
      fields: ds.analysis?.columns?.length || 0,
      hasAnalysis: !!ds.analysis,
      lastUpdate: ds.updatedAt || ds.createdAt
    }));
  } catch (error: any) {
    console.error('Error loading datasources summary:', error);
    return [];
  }
};

/**
 * Get DataSource Model (for star schema)
 */
export interface DataSourceModel {
  dataSourceId: string | number;
  dataSourceName: string;
  tables: Array<{
    name: string;
    columns: Array<{
      name: string;
      type: string;
      nullable?: boolean;
    }>;
  }>;
}

export const getDataSourceModel = async (id: string | number): Promise<DataSourceModel | null> => {
  try {
    const ds = await getDataSourceById(id);
    if (!ds || !ds.analysis) return null;

    // Build model from analysis
    const analysis = ds.analysis;
    
    return {
      dataSourceId: ds.id,
      dataSourceName: ds.name,
      tables: [{
        name: ds.name,
        columns: analysis.columns?.map((f: any) => ({
          name: f.name,
          type: f.inferredType,
          nullable: false
        })) || []
      }]
    };
  } catch (error: any) {
    console.error('Error loading datasource model:', error);
    return null;
  }
};

/**
 * Generate Hefesto model for a single datasource
 */
export const generateHefestoModel = async (id: string) => {
  const res = await api.post(`/datasources/${id}/hefesto`);
  return res;
};

/**
 * Generate Hefesto model from multiple datasources
 */
export const generateMultiHefestoModel = async (datasourceIds: string[]) => {
  const res = await api.post('/datasources/hefesto/multi', { datasourceIds });
  return res;
};

/**
 * Save Hefesto model to datasources
 */
export const saveHefestoModel = async (model: any, datasourceIds: string[]) => {
  const res = await api.post('/datasources/hefesto/save', { model, datasourceIds });
  return res;
};

/**
 * Delete Hefesto model from a datasource
 */
export const deleteHefestoModel = async (datasourceId: string) => {
  const res = await api.delete(`/datasources/${datasourceId}/hefesto`);
  return res;
};

// ============= NEW API REST CREATION FUNCTIONS =============

/**
 * Test API connection before saving
 */
export interface TestApiConnectionConfig {
  url: string;
  authType: 'NONE' | 'API_KEY' | 'BEARER' | 'OAUTH';
  apiKey?: string;
}

export interface TestApiConnectionResponse {
  success: boolean;
  preview?: any;
  error?: string;
  statusCode?: number;
}

export const testApiConnection = async (config: TestApiConnectionConfig): Promise<TestApiConnectionResponse> => {
  try {
    const res = await api.post('/datasources/test-api', config);
    return res.data as TestApiConnectionResponse;
  } catch (error: any) {
    console.error('Error testing API connection:', error);
    return {
      success: false,
      error: error?.response?.data?.message || error?.message || 'Error al probar conexión',
      statusCode: error?.response?.status
    };
  }
};

/**
 * Create new API REST datasource
 */
export interface CreateApiDataSourceConfig {
  name: string;
  url: string;
  authType: 'NONE' | 'API_KEY' | 'BEARER' | 'OAUTH';
  apiKey?: string;
  previewData?: any;
}

export const createApiDataSource = async (config: CreateApiDataSourceConfig): Promise<DataSource> => {
  const res = await api.post('/datasources/api-rest', config);
  return res.data as DataSource;
};

/**
 * Update existing API REST datasource
 */
export const updateApiDataSource = async (id: string | number, config: CreateApiDataSourceConfig): Promise<DataSource> => {
  const res = await api.put(`/datasources/${id}/api-rest`, config);
  return res.data as DataSource;
};

// Export as object for compatibility
export const datasourcesApi = {
  getDataSources,
  getDataSourceById,
  createDataSource,
  updateDataSource,
  deleteDataSource,
  analyzeDataSource,
  getActiveDatasources,
  getDatasources,
  getDataSourceModel,
  generateHefestoModel,
  generateMultiHefestoModel,
  saveHefestoModel,
  deleteHefestoModel,
  testApiConnection,
  createApiDataSource,
  updateApiDataSource
};
